/* eslint-disable react/jsx-fragments */
import React from "react";
import { Modal, Text } from "@networkraildigitalfactory/components";
import useDisableScroll from "../../../hooks/useDisableScroll";
import useUser from "../../../hooks/useUser";
import styles from "./ExampleModal.module.css";

const ExampleModal = (props) => {
  const actionPlanOptions =
    "1. Establish data that is required from line manager\n2. Speak to team members to identify who can provide the data and by what timescales\n3. Create reporting dashboard/template\n4. Communicate with the team new process, timescales\n5. Trial new method and gather feedback\n6. Agree with the team and line manager new process going forward with any amends";
  const safetyPromiseActionPlanOptions =
    "writing in a friendly ‘speak passenger’ tone\nbeing clear and concise\nhaving a colleague who is less involved in the project review my draft document for a second opinion";
  const whatIsSuccessOptions =
    "1. Reporting that is collated on time for every Monday morning by 10am\n2. Collation of data that enables meaningful analysis and actions\n3. Actions that enable the team to improve performance/scorecard measure/service to customers/passengers\n4. Process implemented and being used effectively by 2nd September 2020";
  const developmentGoalOptions =
    "I have investigated some of the options in terms of a change of career direction and I believe that I am suited to a Project Manager role because the role involves dealing with a variety of different types of issues on a day to day basis.";
  const developmentActionPlanOptions =
    "1. Obtain a PM JD\n2. Conduct a gap analysis of my current skills and knowledge compared to those required for the PM role.\n3. Identify a PM to discuss the role and possibly shadow for a day";

  const examplesGBR = {
    objectiveDetail: {
      title: "Objective and goal examples",
      example: (
        <>
          <span className={styles.p}>
            <strong>Example: Review TOC Annual Business Plans (ABPs)</strong> - linked to “Customers
            Choose Rail in Droves”
          </span>
          <span className={styles.p}>
            <strong>When - </strong>Date: 27/01/23
          </span>
          <span className={styles.p}>
            <strong>What - </strong>Revenue growth predictions – conduct reviews of all 14 TOC
            business plans (revenue sections) against the revenue section of the request for
            business plans (RfBP). Identify revenue generative initiatives, gaps, revenue
            abstractive impacts for cost reductions and provide a summary document to DfT on the key
            themes emerging from the TOC annual business plans.
          </span>
          <strong>Why - </strong>A focus on revenue is imperative at a time of cost reduction – to
          ensure revenue is not negatively impacted by cost cutting, maintain a focus on revenue
          within TOCs and DfT and solidify GBRTTs role in assessing the business plans. Identify
          best practice that can be replicated across the industry.
        </>
      ),
    },
    actionPlan: {
      title: "Action plan examples",
      example: (
        <>
          <span className={styles.p}>
            What is your action plan? <strong>How</strong> will you do this?
          </span>
          <span className={styles.p}>
            <strong>Example:</strong> Assemble a team within Passenger Revenue who are able to see
            the TOC plans (ie not impacted by data sharing restrictions), define the revenue section
            for the RfBP, work closely with DfT as plans are created and high level themes emerge.
            Detailed analysis of submitted plans assessing overall industry trends, cross-industry
            benchmarking of TOC RY23/24 and mid-term forecasts against NIRM (GBRTT’s forecasting
            tool) and DfT projections.​ Create one-page summaries for each of the 14x TOCs with
            specific recommendations for the DfT Market Teams to follow up with individual TOCs
          </span>
          <strong>Who - </strong>Working closely with DfT CAP team and Market Leads, TOCs, and
          within GBRTT (BPFM and Industry Commercial).
        </>
      ),
    },
    whatIsSuccess: {
      title: "Success examples",
      example: (
        <>
          <span className={styles.p}>What will success look like?</span>
          <strong>Example:</strong> Detailed analysis of overall industry trends and TOC summaries
          provided to DfT of sufficient quality and insight to enable them to challenge TOCs
          appropriately on their plans to achieve the optimal balance between cost and revenue.
        </>
      ),
    },
    developmentGoal: {
      title: "Development goal examples",
      example: (
        <>
          <span className={styles.text}>
            <strong>
              Example 1 - Behavioural development goal: Listening move from establish to embed.
            </strong>
          </span>
          <span className={styles.text}>
            <strong>Why -</strong> I need to improve my listening skills as there are often
            miscommunications between me and my colleagues.
          </span>
          <span className={styles.p}>
            <strong>What -</strong> I need to move from listening at establish to an embed level.
            The difference is about making time to listen and listening not just for words (see
            behavioural framework)
          </span>
          <span className={styles.text}>
            <strong>Example 2 - Project management skills: Managing stakeholders.</strong>
          </span>
          <span className={styles.text}>
            There are many elements of project management so be clear of your focus.
          </span>
          <span className={styles.text}>
            <strong>Why –</strong> I find in my project that people often say they are not involved.
          </span>
          <strong>What -</strong> I need to explore different stakeholder management tools and trial
          2 different ones within my project.
        </>
      ),
    },
    developmentActionPlan: {
      title: "Action plan examples",
      example: (
        <>
          <span className={styles.text}>
            <strong>
              Example 1 - Behavioural development goal: Listening move from establish to embed:
            </strong>
          </span>
          <span className={styles.text}>
            <strong>How -</strong> Arrange to attend Teams meetings without distractions (for
            example in quiet room and emails turned off). Develop coaching skills- by attending
            coaching course and reading ‘Fierce conversations’ Nancy Klein.
          </span>
          <span className={styles.p}>
            <strong>Who -</strong> Discuss regularly with my line-manager at 121s for feedback.
          </span>
          <span className={styles.text}>
            <strong>Example 2 - Project management skills: Managing stakeholders:</strong>
          </span>
          <span className={styles.text}>
            <strong>How –</strong> review stakeholder management in Managing Successful Programmes
            and attend the collaboration workshops. Determine project and set up use.
          </span>
          <span className={styles.text}>
            <strong>Who -</strong> I need help from our PMO and also from RDG as I am working with
            them.
          </span>
          <strong>When -</strong> I will complete by end June 2023.
        </>
      ),
    },
  };

  const examplesNR = {
    objectiveDetail: {
      title: "Example: What’s the objective / goal?",
      example:
        "Improve management information reporting with the team by establishing a process to ensure all team members are aware of the weekly reporting requirements and are clear on individual requirements.",
    },
    safetyPromise: {
      title: "Example: What’s your Safety Promise?",
      example:
        "My safety promise is…to consider the emotional impact my work might have on another person and ensure I provide all the relevant information when producing a new document so no one is left feeling stressed or uncertain about what it means.",
    },
    safetyPromiseActionPlan: {
      title: "Example: What’s your action plan? How will you achieve this?",
      example: (
        <>
          <p className={styles.safetyActionPlanListHeading}>I will do this by:</p>
          <ul className={styles.safetyActionPlanList}>
            {safetyPromiseActionPlanOptions.split("\n").map((safetyActionPlan, index) => {
              return <li key={index}>{safetyActionPlan}</li>;
            })}
          </ul>
        </>
      ),
    },
    actionPlan: {
      title: "Example: What’s your action plan? How will you achieve this?",
      example: (
        <p>
          {actionPlanOptions.split("\n").map((actionPlan, index) => {
            return <p key={index}>{actionPlan}</p>;
          })}
        </p>
      ),
    },
    whatIsSuccess: {
      title: "Example: What will success look like?",
      example: (
        <p>
          {whatIsSuccessOptions.split("\n").map((successOption, index) => {
            return <p key={index}>{successOption}</p>;
          })}
        </p>
      ),
    },
    developmentGoal: {
      title: "Example: Please describe your development goal",
      example: (
        <p>
          {developmentGoalOptions.split("\n").map((developmentGoal, index) => {
            return <p key={index}>{developmentGoal}</p>;
          })}
        </p>
      ),
    },
    developmentActionPlan: {
      title: "Example: What’s your action plan?",
      example: (
        <p>
          {developmentActionPlanOptions.split("\n").map((developmentActionPlan, index) => {
            return <p key={index}>{developmentActionPlan}</p>;
          })}
        </p>
      ),
    },
  };

  const getExampleByInput = (inputFor, isGBR) => {
    const examples = isGBR ? examplesGBR : examplesNR;
    switch (inputFor) {
      case "objectiveDetail":
      case "safetyPromise":
        return { ...examples[inputFor], focusOnClose: "objectiveDetailBtn" };
      case "actionPlan":
      case "safetyPromiseActionPlan":
      case "developmentGoal":
      case "developmentActionPlan":
        return { ...examples[inputFor], focusOnClose: "actionPlanBtn" };
      case "whatIsSuccess":
        return { ...examples[inputFor], focusOnClose: "successBtn" };
      default:
        return {};
    }
  };

  const { isGBR } = useUser();
  const resultObject = getExampleByInput(props.for, isGBR);
  useDisableScroll(props.show);
  return (
    <Modal
      type="alert"
      cancelText="OK"
      show={props.show}
      onClose={props.close}
      cancelButtonColor="blue"
      shards={props.shards}
      style={{ maxWidth: "90%", width: "453px", top: "5vh", position: "fixed" }}
      focusOnClose={resultObject.focusOnClose}
    >
      <Text
        wrapper="h1"
        type="header"
        style={{
          fontSize: "28px",
          marginBottom: "2.2rem",
          lineHeight: "140%",
          letterSpacing: "0.0015em",
        }}
      >
        {resultObject.title}
      </Text>
      <Text type="body" color="#0E235F">
        {resultObject.example}
      </Text>
    </Modal>
  );
};

export default ExampleModal;
